/**
 * useEstimateBackfill — auto-compute estimatedCredits cho files thiếu cache.
 *
 * Khi nào kích hoạt:
 *  - Project có files
 *  - Mỗi file: nếu thiếu estimatedCredits VÀ có sourceText → fire countBundle 1 lần
 *  - Train mode: cần humanBenchmark để compute audit + semanticMatch factors
 *  - Save vào file.estimatedCredits + persist disk (saveFileImmediately handle)
 *
 * Tránh loop: dùng ref để track files đã backfill trong session.
 */

import { useEffect, useRef } from 'react';
import { useProjectStore } from '../store/useProjectStore';
import { useAccurateTokenCount } from './useAccurateTokenCount';
import { getLineSegments } from '../utils/textUtils';

export function useEstimateBackfill(projectId: string | null) {
    const { countBundle } = useAccurateTokenCount();
    const triedIdsRef = useRef<Set<string>>(new Set());

    const files = useProjectStore(s => s.files);
    const projects = useProjectStore(s => s.projects);
    const updateFile = useProjectStore.getState().updateFile;

    useEffect(() => {
        if (!projectId) return;
        const project = projects.find(p => p.id === projectId);
        if (!project) return;

        const projectFiles = files.filter(f => f.projectId === projectId);
        const isTrain = project.type === 'train';

        for (const file of projectFiles) {
            if (file.estimatedCredits) continue;
            if (!file.sourceText || file.sourceText.trim().length === 0) continue;
            if (triedIdsRef.current.has(file.id)) continue;
            if (isTrain && !file.humanBenchmark) continue; // train cần benchmark

            triedIdsRef.current.add(file.id);

            const segments = getLineSegments(file.sourceText).map(s => s.text);
            const humanSegments = isTrain && file.humanBenchmark
                ? getLineSegments(file.humanBenchmark).map(s => s.text)
                : undefined;

            countBundle({
                bundle_type: isTrain ? 'train' : 'translate',
                segments,
                humanSegments,
                sourceLang: project.sourceLang || 'vi',
                targetLang: project.targetLang || 'en',
            }).then(bundle => {
                if (!bundle) return;
                updateFile(file.id, {
                    estimatedCredits: {
                        translate: bundle.breakdown.translate,
                        qc: bundle.breakdown.qc,
                        semanticMatch: bundle.breakdown.semanticMatch,
                        audit: bundle.breakdown.audit,
                        pricingVersion: bundle.pricingVersion as any,
                        bundleType: bundle.bundleType,
                        computedAt: bundle.computedAt,
                    },
                });
                console.log(`[EstimateBackfill] ${file.fileName}: cached ${bundle.breakdown.translate} credits`);
            }).catch(err => {
                console.warn(`[EstimateBackfill] ${file.fileName} failed:`, err.message);
            });
        }
    }, [projectId, files, projects, countBundle, updateFile]);
}
